import { useEffect, useState } from "react";

import { analytics } from "MVVM/Analytics";
import { useEarnLoop } from "MVVM/Hooks/useEarnLoop";
import { useTrialEligibility } from "MVVM/Hooks/useTrialEligibility";
import { ClipQuota } from "MVVM/Utilities/ClipLimits";
import { TTierKey } from "MVVM/Views/Pricing/Components/PricingCards/types";
import { UnifiedUpgradeModal } from "MVVM/Components/Modals/UnifiedUpgradeModal";
import { ClipLimitModal } from "MVVM/Components/Modals/ClipLimitModal";

interface IClipEligibilityModalProps {
  clipQuota?: ClipQuota;
  userGroup?: string;
  isCs2Clip?: boolean;
  // The creation surface that hit the limit, for per-surface funnel reads.
  surface?: string;
  onClose: () => void;
  closeQuickEditModal?: () => void;
}

const TIER_CLIPS_PER_MONTH: Record<string, string> = {
  ESSENTIALS: "10",
  PRO: "50",
  CREATOR_PLAT: "Unlimited",
};

// Earning a clip only helps where a clip is the thing being blocked. Montages
// and the timeline editor are gated by tier, so a free player never reaches
// their clip limit there, and Match VOD is not a free surface. All three keep
// the pricing modal.
const EARNABLE_SURFACES = [
  "Quick Edit",
  "Remix",
  "Match Page",
  "Match History",
];

const getTierCopy = (userGroup?: string) => {
  switch (userGroup) {
    case "ESSENTIALS":
      return {
        featureName: "Clip Limit Reached",
        upgradeTierKey: "PRO" as TTierKey,
      };
    case "PRO":
    case "PRO_PLUS":
      return {
        featureName: "Clip Limit Reached",
        upgradeTierKey: "CREATOR_PLAT" as TTierKey,
      };
    case "CREATOR_PLAT":
      return {
        featureName: "Clip Limit Reached",
        upgradeTierKey: null,
      };
    case "FREE":
    default:
      return {
        featureName: "Clip Limit Reached",
        upgradeTierKey: "ESSENTIALS" as TTierKey,
      };
  }
};

export const ClipEligibilityModal = ({
  clipQuota,
  userGroup,
  isCs2Clip,
  surface = "Unknown",
  onClose,
  closeQuickEditModal,
}: IClipEligibilityModalProps) => {
  const { isTrialEligible } = useTrialEligibility();
  const earnLoop = useEarnLoop();
  const [showPricing, setShowPricing] = useState(false);
  const tierCopy = getTierCopy(userGroup);
  const nextAvailableAt = clipQuota?.nextAvailableAt;

  useEffect(() => {
    analytics.track("Clip Eligibility Modal Viewed", {
      currentTier: userGroup,
      upgradeTier: tierCopy.upgradeTierKey,
      isTrialEligible,
      hasQuotaTimer: !!nextAvailableAt,
    });
  }, [userGroup, tierCopy.upgradeTierKey, isTrialEligible, nextAvailableAt]);

  const handleClose = () => {
    onClose();
    closeQuickEditModal?.();
  };

  // Free players get the earn-first modal; every other gate keeps the pricing
  // modal it has always had. The earn arm of the ALL-2079 test, plus internal QA.
  if (
    earnLoop.enabled &&
    userGroup === "FREE" &&
    EARNABLE_SURFACES.includes(surface) &&
    !showPricing
  ) {
    return (
      <ClipLimitModal
        surface={surface}
        onResume={onClose}
        onUpgrade={() => setShowPricing(true)}
      />
    );
  }

  const upgradeTier = tierCopy.upgradeTierKey || "ESSENTIALS";
  const clipsLabel = TIER_CLIPS_PER_MONTH[upgradeTier] || "More";
  const featureName =
    clipsLabel === "Unlimited"
      ? "Create Unlimited Clips"
      : `Create ${clipsLabel} Clips per Month`;

  return (
    <UnifiedUpgradeModal
      featureName={featureName}
      upgradeAction="Create More Clips"
      requiredTierKey={upgradeTier}
      analyticsSource="Clip Eligibility"
      showDiscordBotCta
      showMonthlyRewardsCta={earnLoop.enabled}
      isCs2Clip={isCs2Clip}
      onClose={handleClose}
    />
  );
};
